import React from 'react';
import PropTypes from 'prop-types';
import { LuLandmark } from 'react-icons/lu';
import Badge from '../../../../components/ui/Badge';
import { statusGroupOf } from '../bankAccountColumns';

const TONES = { active: 'success', passive: 'danger', pending: 'warning', other: 'info' };
const MUTED = 'tw-text-slate-400 dark:tw-text-slate-500';

/** The stored status text, coloured by what it says (aktif / pasif / bekliyor). */
export const AccountStatusBadge = ({ status }) => {
  if (!status) return <span className={MUTED}>-</span>;
  return (
    <Badge tone={TONES[statusGroupOf(status)]}>
      <span aria-hidden="true" className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-current" />
      {status}
    </Badge>
  );
};

AccountStatusBadge.propTypes = { status: PropTypes.string };

export const AccountIcon = ({ size = 'sm' }) => (
  <span aria-hidden="true" className={`tw-grid tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-sky-400 tw-to-blue-600 tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${size === 'lg' ? 'tw-h-10 tw-w-10' : 'tw-h-7 tw-w-7'}`}>
    <LuLandmark className={size === 'lg' ? 'tw-h-[18px] tw-w-[18px]' : 'tw-h-3.5 tw-w-3.5'} />
  </span>
);

AccountIcon.propTypes = { size: PropTypes.oneOf(['sm', 'lg']) };

/** Content of one bank account table cell. */
const BankAccountCell = ({ columnKey, account }) => {
  const value = account[columnKey];
  switch (columnKey) {
    case 'firma':
      return (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
          <AccountIcon />
          <span className="tw-min-w-0 tw-truncate tw-font-semibold tw-text-slate-900 dark:tw-text-white">{value || '-'}</span>
        </span>
      );
    case 'hesapNo':
      return value ? <span className="tw-block tw-truncate tw-font-mono tw-text-[13px] tw-tracking-wide">{value}</span> : <span className={MUTED}>-</span>;
    case 'durum':
      return <AccountStatusBadge status={value} />;
    default:
      return value ? <span className="tw-block tw-truncate">{value}</span> : <span className={MUTED}>-</span>;
  }
};

BankAccountCell.propTypes = { columnKey: PropTypes.string.isRequired, account: PropTypes.object.isRequired };

/** One row of the card list (narrow screens). */
export const BankAccountCardRow = ({ account }) => (
  <>
    <AccountIcon size="lg" />
    <span className="tw-min-w-0 tw-flex-1">
      <span className="tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-2">
        <span className="tw-min-w-0 tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{account.firma || '-'}</span>
        <AccountStatusBadge status={account.durum} />
      </span>
      <span className="tw-mt-0.5 tw-block tw-truncate tw-font-mono tw-text-[12px] tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400">{account.hesapNo || '-'}</span>
      <span className="tw-mt-0.5 tw-block tw-truncate tw-text-[13px] tw-text-slate-500 dark:tw-text-slate-400">
        {[account.sube, account.odemeTuru, account.odemeDonemi].filter(Boolean).join(' · ') || '-'}
      </span>
    </span>
  </>
);

BankAccountCardRow.propTypes = { account: PropTypes.object.isRequired };

export default BankAccountCell;
